import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Footer from '../../Footer/Footer';

const C = {
  navy: '#101d40',
  midnight: '#0A1428',
  gold: '#C9963C',
  ivory: '#FAF7F0',
  muted: 'rgba(28,28,30,0.72)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const Inner = styled.article`
  max-width: 760px;
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 2rem) clamp(4rem, 8vw, 6rem);
`;

const Back = styled(Link)`
  display: inline-block;
  margin: 0 0 1.25rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: ${C.navy};
  text-decoration: none;
`;

const Kicker = styled.p`
  margin: 0 0 0.55rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Title = styled.h1`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  font-weight: 700;
  line-height: 1.02;
  color: ${C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const Lead = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 1.05rem;
  line-height: 1.7;
  color: ${C.muted};
`;

const Block = styled.section`
  margin: 0 0 1.75rem;

  h2 {
    margin: 0 0 0.55rem;
    font-family: ${p => p.theme.fonts.main};
    font-size: 1.55rem;
    font-weight: 700;
    color: ${C.navy};
  }

  p, li {
    font-family: 'Inter', -apple-system, sans-serif;
    font-size: 0.98rem;
    line-height: 1.7;
    color: ${C.muted};
  }

  p { margin: 0 0 0.75rem; }

  ul {
    margin: 0 0 0.75rem;
    padding-left: 1.15rem;
  }

  li { margin: 0 0 0.4rem; }

  a {
    color: ${C.navy};
    font-weight: 600;
  }
`;

const MiceTrailTerms = () => (
  <>
    <Page className="christmas-page">
      <Helmet>
        <title>Mice Trail terms | Christmas 2026 at Belvoir Castle</title>
        <meta name="description" content="Terms for the Belvoir Castle Mice Trail. Find a mouse in a town, keep it, register it, and win a free family ticket. The Castle trail is included with Deck the Halls." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/mice-trail/terms" />
      </Helmet>
      <Inner>
        <Back to="/christmas/mice-trail">← The Mice Trail</Back>
        <Kicker>Christmas 2026</Kicker>
        <Title>Mice Trail <em>terms</em></Title>
        <Lead>
          These terms cover the Belvoir Mice Trail: the search through five towns, and the trail inside the Castle. Taking part means you agree to them. The promoter is Belvoir Castle, Grantham, Leicestershire, NG32 1PE.
        </Lead>

        <Block>
          <h2>The town search</h2>
          <p>The mice travel through five towns on the way back to Belvoir, one town each week. Ten mice are hidden in each town. We name the town when that week begins, and the mice move on at the end of it.</p>
          <ul>
            <li>You do not need to buy anything to join the town search.</li>
            <li>Search only in places that are open to you. Follow the rules of any shop, museum or garden, and do not climb, force a way in, or go behind a barrier.</li>
            <li>Children must be with an adult.</li>
            <li>A mouse you find is yours to keep. Leave everything else as you found it.</li>
          </ul>
        </Block>

        <Block>
          <h2>Registering a mouse</h2>
          <p>Each mouse has one QR code. Scan it and register the mouse on the page that opens. A mouse can be registered once, by the first successful registration. A later find of the same mouse does not win a prize.</p>
          <ul>
            <li>You need your name, a name for the mouse, and an email address you can read. Telling us who you were with is optional.</li>
            <li>If you are under 18, a parent or guardian must agree to these terms and complete the registration.</li>
            <li>The mouse’s name, and your name, must be suitable for a family activity. We can refuse a registration that is offensive, made up, or not a genuine find.</li>
            <li>Check the details before you submit. A registration cannot be changed or done again.</li>
          </ul>
        </Block>

        <Block>
          <h2>The family ticket</h2>
          <p>If you are the first person to find a town mouse and register it successfully, you win one free family ticket to Deck the Halls at Belvoir Castle for Christmas 2026.</p>
          <ul>
            <li>One prize for each registered mouse. Finding more than one mouse, and registering each of them, can win more than one ticket.</li>
            <li>We email the address you registered to explain how to book, which dates are open, and who the ticket admits. The visit must fall between 14 November and 30 December 2026, on a day the Castle is open, and is subject to availability.</li>
            <li>There is no cash alternative. The ticket cannot be sold, and it is not valid for other experiences such as Santa’s Grotto, afternoon tea or parking.</li>
            <li>Once the visit is booked, our usual <Link to="/terms">admission terms</Link> apply.</li>
            <li>Employees of Belvoir Castle, anyone who helped hide the mice, and the people who live with them, may join the search. They are not eligible for the family ticket.</li>
            <li>If we cannot reach the email you gave us, we may not be able to issue the ticket.</li>
          </ul>
        </Block>

        <Block>
          <h2>The trail inside the Castle</h2>
          <p>From 14 November to 30 December 2026, the Mice Trail inside Belvoir Castle is included with a Deck the Halls entry ticket. Collect a trail sheet when you arrive. Any prize for finishing the sheet is redeemed at the Castle on the day of your visit, before you leave, while it is available. That prize is separate from the town-search family ticket.</p>
        </Block>

        <Block>
          <h2>Photos and your details</h2>
          <p>We use your name, email, the mouse’s name, and any note of who you were with, to record the find and to send the prize. An optional tick lets us email you about Christmas at Belvoir. You can say no to that and still register.</p>
          <p>A photo stays on your phone unless you tick the box that lets us use it on social media. Only tick that box if everyone in the photo is happy for us to share it. We also keep a scrambled record of the network address used to register, so the same mouse cannot be claimed over and over.</p>
          <p>More on how we handle personal information is in our <Link to="/privacy-policy">privacy policy</Link>.</p>
        </Block>

        <Block>
          <h2>Changes, and if something goes wrong</h2>
          <p>We may move the remaining mice, shorten a town’s week, or close the search. A mouse that has not been registered before it is collected cannot be claimed later. A prize already won by a valid registration still stands.</p>
          <p>Belvoir Castle is not responsible for travel costs, for a venue being closed, or for an injury or loss that happens while you are searching, except where the law does not allow us to exclude it. These terms are governed by the law of England and Wales.</p>
        </Block>

        <Block>
          <h2>Questions</h2>
          <p>
            Email <a href="mailto:info@belvoircastle.com">info@belvoircastle.com</a><br />
            Belvoir Castle, Grantham, Leicestershire, NG32 1PE
          </p>
        </Block>
      </Inner>
    </Page>
    <Footer />
  </>
);

export default MiceTrailTerms;
